import { useEffect, useState } from 'react'
import { urlSigneePiece } from '../../api/candidatures'
import { messageErreur } from '../../onboarding/erreurs'
import { assuranceRecevable, casierRecevable, LIBELLE_STATUT_PIECE } from '../../onboarding/pieces'
import type { PieceDossier } from '../../onboarding/types'
import { dateCourte } from '../Format'
import { Erreur, useAction } from './Briques'

/**
 * Pièces d'un dossier : liste, aperçu par URL signée (bucket privé, 5 min),
 * validation / rejet motivé, et — pour le livreur — l'AVERTISSEMENT sur un
 * casier de plus de 90 jours ou une assurance échue, affiché AVANT d'appeler
 * la base. La base reste l'autorité : elle refusera de toute façon, et son
 * refus est traduit.
 */

const COULEUR_PIECE = { valide: 'green', rejete: 'red', en_attente: 'yellow' } as const

/** Avertissement de confort sur une pièce pas encore validée (livreur). */
function avertissement(p: PieceDossier): string | null {
  if (p.statut === 'valide') return null
  const aujourdhui = new Date()
  if (p.type === 'criminal_record') {
    if (!p.emisLe) return "La date d'émission de l'extrait n'est pas renseignée : la base refusera la validation."
    const r = casierRecevable(p.emisLe, aujourdhui)
    if (r.ok) return null
    if (r.raison === 'trop_ancien') {
      return `Extrait émis il y a ${r.jours} jours (plus de 90) : la base refusera la validation. Rejetez la pièce en demandant un extrait récent.`
    }
    if (r.raison === 'date_future') return "La date d'émission est dans le futur : vérifiez la pièce."
    return "La date d'émission est illisible."
  }
  if (p.type === 'insurance') {
    if (!p.expireLe) return "L'échéance de l'assurance n'est pas renseignée : la base refusera la validation."
    if (!assuranceRecevable(p.expireLe, aujourdhui)) {
      return `Assurance échue le ${dateCourte(p.expireLe)} : la base refusera la validation.`
    }
  }
  return null
}

function ApercuPiece({ chemin }: { chemin: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    let vivant = true
    urlSigneePiece(chemin).then(
      (u) => vivant && setUrl(u),
      (e: unknown) => vivant && setErreur(messageErreur(e)),
    )
    return () => { vivant = false }
  }, [chemin])

  if (erreur) return <Erreur texte={erreur} />
  if (!url) return <p className="dos-note">Chargement de l'aperçu…</p>
  const ext = (chemin.split('.').pop() ?? '').toLowerCase()
  return (
    <div className="piece-apercu">
      {ext === 'pdf' ? (
        <iframe src={url} title="Aperçu du document" />
      ) : ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? (
        <img src={url} alt="Aperçu du document" />
      ) : (
        <p className="dos-note">Ce format ne s'affiche pas ici.</p>
      )}
      <p className="dos-note" style={{ marginTop: 4 }}>
        <a href={url} target="_blank" rel="noreferrer noopener">Ouvrir dans un nouvel onglet</a> — lien valable 5 minutes.
      </p>
    </div>
  )
}

function LignePiece({
  piece,
  avertir,
  surValider,
  surRejeter,
}: {
  piece: PieceDossier
  avertir: boolean
  surValider: (p: PieceDossier) => Promise<void>
  surRejeter: (p: PieceDossier, motif: string) => Promise<void>
}) {
  const [apercu, setApercu] = useState(false)
  const [rejet, setRejet] = useState(false)
  const [motif, setMotif] = useState('')
  const { occupe, erreur, lancer, signaler } = useAction()
  const alerte = avertir ? avertissement(piece) : null

  return (
    <div className="piece">
      <div className="piece-tete">
        <span className="nom">{piece.libelle}</span>
        <span className={`badge badge-${COULEUR_PIECE[piece.statut]}`}>{LIBELLE_STATUT_PIECE[piece.statut]}</span>
        <div className="piece-actions">
          <button className="btn btn-outline btn-sm" onClick={() => setApercu((a) => !a)}>
            {apercu ? "Masquer l'aperçu" : 'Aperçu'}
          </button>
          {piece.statut !== 'valide' && (
            <button className="btn btn-green btn-sm" disabled={occupe} onClick={() => void lancer(() => surValider(piece))}>
              Valider
            </button>
          )}
          {piece.statut !== 'rejete' && (
            <button className="btn btn-outline btn-sm" disabled={occupe} onClick={() => setRejet((r) => !r)}>
              Rejeter
            </button>
          )}
        </div>
      </div>
      <div className="piece-detail">
        Déposée le {dateCourte(piece.deposeLe)}
        {piece.emisLe && <> · émise le {dateCourte(piece.emisLe)}</>}
        {piece.expireLe && <> · échéance le {dateCourte(piece.expireLe)}</>}
        {piece.motifRejet && <> · motif du rejet : « {piece.motifRejet} »</>}
      </div>
      {alerte && <div className="dos-alerte">{alerte}</div>}
      {rejet && (
        <div className="dos-champs">
          <input
            className="grow"
            placeholder="Motif du rejet (obligatoire, le candidat le lira)"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          <button
            className="btn btn-danger-sm btn-sm"
            disabled={occupe}
            onClick={() => {
              if (!motif.trim()) {
                signaler('Indiquez le motif du rejet de la pièce.')
                return
              }
              void lancer(async () => {
                await surRejeter(piece, motif)
                setRejet(false)
                setMotif('')
              })
            }}
          >
            Confirmer le rejet
          </button>
        </div>
      )}
      <Erreur texte={erreur} />
      {apercu && <ApercuPiece chemin={piece.chemin} />}
    </div>
  )
}

export function ListePieces({
  pieces,
  requises,
  facultatives = [],
  libelles,
  avertirValidite,
  surValider,
  surRejeter,
}: {
  pieces: PieceDossier[]
  requises: readonly string[]
  facultatives?: readonly string[]
  libelles: Record<string, string>
  /** Livreur : casier > 90 jours, assurance échue. */
  avertirValidite: boolean
  surValider: (p: PieceDossier) => Promise<void>
  surRejeter: (p: PieceDossier, motif: string) => Promise<void>
}) {
  const manquantes = requises.filter((t) => !pieces.some((p) => p.type === t))
  return (
    <>
      {manquantes.map((t) => (
        <div className="piece" key={`manque-${t}`}>
          <div className="piece-tete">
            <span className="nom">{libelles[t] ?? t}</span>
            <span className="badge badge-gray">Non déposée</span>
          </div>
        </div>
      ))}
      {pieces.map((p) => (
        <LignePiece key={p.id} piece={p} avertir={avertirValidite} surValider={surValider} surRejeter={surRejeter} />
      ))}
      {pieces.length === 0 && manquantes.length === 0 && <p className="dos-note">Aucune pièce déposée.</p>}
      {facultatives.length > 0 && (
        <p className="dos-note" style={{ marginTop: 8 }}>
          Facultative : {facultatives.map((t) => libelles[t] ?? t).join(', ')}.
        </p>
      )}
    </>
  )
}
